import React, { useEffect } from 'react';
import logoImg from '../landing/logo.png';
import montrealImg from '../landing/montreal-small.png';
import jrobImg from '../landing/jrob.png';
import computerImg from '../landing/computer.png';
import mhmLogoImg from '../landing/mhm-montreal-logo.png';

const LandingPage = () => {
  useEffect(() => {
    // Intersection Observer for section animations
    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px 0px -20% 0px'
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const section = entry.target.querySelector('.content-section');
        const sideHeading = document.querySelector(`.side-heading[data-heading="${section?.dataset.section}"]`);
        const sideImage = entry.target.querySelector('.side-image');

        if (entry.isIntersecting) {
          section?.classList.add('visible');
          if (sideHeading) {
            // Hide all side headings first
            document.querySelectorAll('.side-heading').forEach(h => {
              h.classList.remove('visible');
            });
            // Show current one with bounce animation
            sideHeading.classList.add('visible');
          }
          if (sideImage) {
            sideImage.classList.add('visible');
          }
        } else {
          // Hide heading and image when leaving section
          if (sideHeading) {
            sideHeading.classList.remove('visible');
          }
          if (sideImage) {
            sideImage.classList.remove('visible');
          }
        }
      });
    }, observerOptions);

    // Observe all sections except hero
    const sections = document.querySelectorAll('.landing-section:not(.hero-section)');
    sections.forEach(section => {
      sectionObserver.observe(section);
    });

    return () => {
      sections.forEach(section => {
        sectionObserver.unobserve(section);
      });
    };
  }, []);

  return (
    <div className="landing-page">
      {/* Side Headings - Independent from sections */}
      <div className="side-heading" data-heading="mission">
        <span>Mission</span>
      </div>
      <div className="side-heading" data-heading="vision">
        <span>Vision</span>
      </div>
      <div className="side-heading" data-heading="founder">
        <span>Founder</span>
      </div>
      <div className="side-heading" data-heading="join">
        <span>Support</span>
      </div>

      {/* Hero Section */}
      <section className="landing-section hero-section">
        <div className="logo">
          <img src={logoImg} alt="Music History Museum of Montreal Logo" />
        </div>
        
        <div className="hero-text">
          <h1>MUSIC HISTORY MUSEUM OF MONTREAL</h1>
          <p className="hero-description">
            Montreal's music scene has shaped generations, yet so many stories remain untold, so many venues exist only in memory, and countless contributions risk being forgotten. The Montreal Music History Museum aims to change that by creating a dynamic, community-driven archive that celebrates every corner of our city's sonic legacy.
          </p>
        </div>

        <div className="scroll-indicator">
          <svg viewBox="0 0 24 24">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
        </div>
      </section>

      {/* Mission Section */}
      <section className="landing-section">
        <div className="content-section" data-section="mission">
          <h2>Our Mission</h2>
          <p>
            We're working to preserve, celebrate, and share the rich tapestry of Montreal's musical history through accessible archives, immersive experiences, and meaningful community engagement.
          </p>
          <p>
            This isn't just about looking back. It's about ensuring the stories that shaped our culture continue to inspire future generations.
          </p>
        </div>
        <div className="side-image" data-heading="mission">
          <img src={montrealImg} alt="Montreal" />
        </div>
      </section>

      {/* What We're Building Section */}
      <section className="landing-section">
        <div className="content-section visible" data-section="vision">
          <h2>What We're Building</h2>

          <h3>Preserving the Stories</h3>
          <p>
            At our core, we plan to document Montreal's music through oral history interviews with artists, venue owners, engineers, and collectives who built the scene. These conversations will feed into publicly accessible Wikipedia-style articles and a comprehensive multimedia database featuring photos, flyers, setlists, recordings, and video clips. A living archive for everyone.
          </p>

          <h3>Bringing History to Life</h3>
          <p>
            We envision themed exhibits exploring everything from the punk era to jazz history and electronic pioneers. Neighbourhood-focused installations will dive deep into the musical identity of Montreal's diverse communities. Through travelling pop-ups at venues, libraries, and festivals we want to make history tangible and immersive.
          </p>

          <h3>Music & Montreal</h3>
          <p>
            Music deserves to be heard. We plan to curate seasonal mixtapes spanning genres and eras, produce zines highlighting niche scenes and artist profiles, offer microgrants to support independent artists, and launch podcast series that offer deep-dive narratives and interview highlights.
          </p>

          <h3>Collaboration & Growth</h3>
          <p>
            The museum will thrive through partnerships. We hope to work with labels on vinyl and tape reissues complete with archival liner notes, host artist residencies where creators draw inspiration from our archives, and develop programs with schools and universities to bring music history into classrooms.
          </p>

          <h3>Community at the Heart</h3>
          <p>
            Live events will anchor our community engagement: writing camps where lyricists and historians collaborate, "Story & Song" nights pairing live interviews with performances, and benefit concerts supporting the archive and at-risk venues. We're also exploring walking tours as extensions of neighbourhood exhibitions, and using QR codes to unlock music history across the city.
          </p>

          <h3>Digital Reach</h3>
          <p>
            In the digital realm, we plan to produce YouTube & podcast series documenting people, venues and more while additionally sharing bite-sized stories on TikTok and Instagram.
          </p>

          <h3>Just for Fun</h3>
          <p>
            Because music is meant to be enjoyed, we're dreaming up DJ Hero/Guitar Hero nights, karaoke nights, trivia nights, and other entertainment events that bring the community together in celebration.
          </p>
        </div>
        <div className="side-image" data-heading="vision">
          <img src={computerImg} alt="Vision" />
        </div>
      </section>

      {/* Founder Section */}
      <section className="landing-section">        
        <div className="content-section" data-section="founder">
          <p>
            <span className="title-emphasis">J-Rob</span> is a Montreal-based multidisciplinary artist, musician, and community organizer with over a decade of experience documenting and preserving cultural narratives.
          </p>
          <p>
            As a former President of Revive NCC/Centre for Canadians of African Descent and former Executive Assistant at the Museum of Jewish Montreal, he has dedicated his career to safeguarding community histories and fostering cultural connection.
          </p>
          <p>
            Through his event organizing work with MASON's Kitchen and Favourite Library, J-Rob has created platforms for local artists while building networks within Montreal's diverse music scene.
          </p>
        </div>
        <div className="side-image" data-heading="founder">
          <img src={jrobImg} alt="Jared Roboz" />
        </div>
      </section>

      {/* Join Us Section */}
      <section className="landing-section">
        <div className="content-section" data-section="join">
          <h2>Join Us</h2>
          <p>
            This vision is ambitious, but it's rooted in love for this city and its incredible musical heritage. The Montreal Music History Museum will be built by and for the community: artists, fans, historians, and dreamers alike.
          </p>
          <p>
            Whether you have stories to share, want to collaborate, or simply want to support the preservation of Montreal's music, we'd love to hear from you.
          </p>
          <p>
            Let's build something that honours where we've been and inspires where we're going.
          </p>
          <p>
            <strong>Peace.</strong>
          </p>
          <p>
            <em>Interested in getting involved? Contact us at</em><br />
            <a href="mailto:j-rob@mhmmontreal.ca">j-rob@mhmmontreal.ca</a>
          </p>

          <div style={{marginTop:'2rem', borderRadius:'12px', overflow:'hidden', boxShadow:'0 4px 12px rgba(0,0,0,0.1)', maxWidth:'min(400px, 90vw)', margin:'2rem auto 0 auto'}}>
            <iframe
              id='kofiframe'
              src='https://ko-fi.com/mhmmontreal/?hidefeed=true&widget=true&embed=true&preview=true'
              style={{border:'none', width:'100%', display:'block'}}
              height='500'
              title='mhmmontreal'
            />
          </div>
        </div>
        <div className="side-image" data-heading="join">
          <img src={mhmLogoImg} alt="MHM Montreal" />
        </div>
      </section>
    </div>
  );
};

export default LandingPage;